import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { createFreelancerApi, CreateFreelancerRequest } from "../freelancerApi";

import ecoLogo from "../../../img/eco-logo.png";

import "./freelancer-register.css";

const onlyDigits = (v: string) => v.replace(/\D/g, "");

const parseStoreIds = (raw: string): string[] => {
  const parts = raw
    .split(/[\s,;]+/g)
    .map((s) => s.trim())
    .filter(Boolean);

  const unique: string[] = [];
  const seen = new Set<string>();
  for (const p of parts) {
    const v = p.toLowerCase();
    if (!seen.has(v)) {
      seen.add(v);
      unique.push(p);
    }
  }
  return unique;
};

const formatCpf = (value: string) => {
  const digits = onlyDigits(value).slice(0, 11);

  const p1 = digits.slice(0, 3);
  const p2 = digits.slice(3, 6);
  const p3 = digits.slice(6, 9);
  const p4 = digits.slice(9, 11);

  let out = p1;
  if (p2) out += `.${p2}`;
  if (p3) out += `.${p3}`;
  if (p4) out += `-${p4}`;

  return out;
};

const formatContato = (value: string) => {
  const digits = onlyDigits(value).slice(0, 11);

  const ddd = digits.slice(0, 2);
  const p1 = digits.slice(2, 7);
  const p2 = digits.slice(7, 11);

  let out = "";
  if (ddd) out = `(${ddd}`;
  if (ddd.length === 2) out += `)`;
  if (p1) out += ` ${p1}`;
  if (p2) out += `-${p2}`;

  return out.trim();
};

const FreelancerPage: React.FC = () => {
  const navigate = useNavigate();
  const [nomeCompleto, setNomeCompleto] = useState("");
  const [cpf, setCpf] = useState("");
  const [contatoDigits, setContatoDigits] = useState("");

  const [pix, setPix] = useState("");
  const [banco, setBanco] = useState("");

  const [rua, setRua] = useState("");
  const [numero, setNumero] = useState("");
  const [complemento, setComplemento] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [estado, setEstado] = useState<string>("");

  const [storeIdsRaw, setStoreIdsRaw] = useState("");

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [aceitePagamento, setAceitePagamento] = useState(false);

  const cpfDigits = useMemo(() => onlyDigits(cpf), [cpf]);
  const handleContatoKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key !== "Backspace") return;

    const el = e.currentTarget;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    if (start !== end) return;
    if (start === 0) return;

    const val = el.value;
    if (/\D/.test(val[start - 1])) {
      e.preventDefault();
      const digitsBefore = onlyDigits(val.slice(0, start)).length;
      if (digitsBefore <= 0) return;
      setContatoDigits((prev) => prev.slice(0, digitsBefore - 1) + prev.slice(digitsBefore));
    }
  };
  const storeIds = useMemo(() => parseStoreIds(storeIdsRaw), [storeIdsRaw]);

  const canSubmit = useMemo(() => {
    return (
      nomeCompleto.trim().length > 0 &&
      cpfDigits.length === 11 &&
      contatoDigits.length === 11 &&
      pix.trim().length > 0 &&
      banco.trim().length > 0 &&
      rua.trim().length > 0 &&
      numero.trim().length > 0 &&
      bairro.trim().length > 0 &&
      cidade.trim().length > 0 &&
      estado.trim().length > 0 &&
      aceitePagamento &&
      !loading
    );
  }, [
    nomeCompleto,
    cpfDigits,
    contatoDigits,
    pix,
    banco,
    rua,
    numero,
    bairro,
    cidade,
    estado,
    aceitePagamento,
    loading,
  ]);

  const resetForm = () => {
    setNomeCompleto("");
    setCpf("");
    setContatoDigits("");
    setPix("");
    setBanco("");
    setRua("");
    setNumero("");
    setComplemento("");
    setBairro("");
    setCidade("");
    setEstado("");
    setStoreIdsRaw("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);

    if (!nomeCompleto.trim()) {
      toast.error("Informe seu nome completo.");
      return;
    }

    if (cpfDigits.length !== 11) {
      toast.error("CPF inválido. Informe 11 dígitos.");
      return;
    }

    if (contatoDigits.length !== 11) {
      toast.error("Contato inválido. Informe (11) 99999-9999.");
      return;
    }

    if (!estado.trim()) {
      toast.error("Selecione o estado (UF).");
      return;
    }

    if (!aceitePagamento) {
      setErrorMsg("Você precisa confirmar o aceite do prazo de pagamento.");
      return;
    }

    const dto: CreateFreelancerRequest = {
      nomeCompleto: nomeCompleto.trim(),
      cpf: cpfDigits,
      contato: formatContato(contatoDigits),
      pix: pix.trim() || undefined,
      banco: banco.trim() || undefined,
      rua: rua.trim() || undefined,
      numero: numero.trim() || undefined,
      complemento: complemento.trim() || undefined,
      bairro: bairro.trim() || undefined,
      cidade: cidade.trim() || undefined,
      estado: estado || undefined,
      storeIds: storeIds.length ? storeIds : undefined,
    };

    setLoading(true);
    try {
      await createFreelancerApi(dto);
      toast.success("Cadastro realizado com sucesso!");
      resetForm();
      navigate("/freelancer-login", { replace: true });
    } catch (err: any) {
      const msg = err?.message ?? "Não foi possível realizar o cadastro.";
      toast.error(msg);
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="StoreMenuPage">
      <div className="LoginLogoWrap">
        <img className="LoginLogo" src={ecoLogo} alt="ECO System Serviços" />
      </div>

      <div className="freelancer-page freelancer-page--orange">
        <div className="freelancer-container">
          <h1 className="StoreMenuTitle">Cadastro de Freelancer</h1>
          <p className="freelancer-pageSubtitle">Realize aqui seu cadastro</p>

          <div className="freelancer-card">
            <p className="freelancer-subtitle">Preencha seus dados abaixo</p>

            <form className="freelancer-form" onSubmit={handleSubmit}>
              <section className="freelancer-section">
                <h2 className="freelancer-section__title">Identificação</h2>

                <label className="freelancer-label">
                  <span className="freelancer-labelTitle">Nome Completo</span>

                  <input
                    className="freelancer-input"
                    value={nomeCompleto}
                    onChange={(e) => setNomeCompleto(e.target.value)}
                    placeholder="Nome completo"
                    autoComplete="name"
                    disabled={loading}
                  />
                </label>

                <label className="freelancer-label">
                  <span className="freelancer-labelTitle">CPF</span>

                  <input
                    className="freelancer-input"
                    value={cpf}
                    onChange={(e) => setCpf(formatCpf(e.target.value))}
                    placeholder="000.000.000-00"
                    inputMode="numeric"
                    autoComplete="off"
                    disabled={loading}
                  />
                </label>

                <label className="freelancer-label">
                  <span className="freelancer-labelTitle">Contato (celular)</span>

                  <input
                    className="freelancer-input"
                    value={formatContato(contatoDigits)}
                    onChange={(e) => setContatoDigits(onlyDigits(e.target.value).slice(0, 11))}
                    onKeyDown={handleContatoKeyDown}
                    placeholder="(11) 99999-9999"
                    inputMode="numeric"
                    autoComplete="tel"
                    disabled={loading}
                  />
                </label>
              </section>

              <section className="freelancer-section">
                <h2 className="freelancer-section__title">Pagamento</h2>

                <label className="freelancer-label">
                  Chave Pix
                  <input
                    className="freelancer-input"
                    value={pix}
                    onChange={(e) => setPix(e.target.value)}
                    placeholder="Chave Pix"
                    autoComplete="off"
                    disabled={loading}
                  />
                </label>

                <label className="freelancer-label">
                  Banco
                  <input
                    className="freelancer-input"
                    value={banco}
                    onChange={(e) => setBanco(e.target.value)}
                    placeholder="Banco"
                    autoComplete="off"
                    disabled={loading}
                  />
                </label>
              </section>

              <section className="freelancer-section">
                <h2 className="freelancer-section__title">Endereço</h2>

                <label className="freelancer-label">
                  Rua
                  <input
                    className="freelancer-input"
                    value={rua}
                    onChange={(e) => setRua(e.target.value)}
                    placeholder="Rua / Avenida"
                    autoComplete="street-address"
                    disabled={loading}
                  />
                </label>

                <div className="freelancer-grid">
                  <label className="freelancer-label">
                    Número
                    <input
                      className="freelancer-input"
                      value={numero}
                      onChange={(e) => setNumero(e.target.value)}
                      placeholder="Nº"
                      autoComplete="off"
                      disabled={loading}
                    />
                  </label>

                  <label className="freelancer-label">
                    Complemento
                    <input
                      className="freelancer-input"
                      value={complemento}
                      onChange={(e) => setComplemento(e.target.value)}
                      placeholder="Apto / Bloco / Referência"
                      autoComplete="off"
                      disabled={loading}
                    />
                  </label>
                </div>

                <div className="freelancer-grid">
                  <label className="freelancer-label">
                    Bairro
                    <input
                      className="freelancer-input"
                      value={bairro}
                      onChange={(e) => setBairro(e.target.value)}
                      placeholder="Bairro"
                      autoComplete="address-level3"
                      disabled={loading}
                    />
                  </label>

                  <label className="freelancer-label">
                    Cidade
                    <input
                      className="freelancer-input"
                      value={cidade}
                      onChange={(e) => setCidade(e.target.value)}
                      placeholder="Cidade"
                      autoComplete="address-level2"
                      disabled={loading}
                    />
                  </label>
                </div>

                <label className="freelancer-label">
                  Estado (UF)
                  <select
                    className="freelancer-input"
                    value={estado}
                    onChange={(e) => setEstado(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Selecione...</option>
                    <option value="RJ">RJ</option>
                    <option value="SP">SP</option>
                  </select>
                </label>
              </section>

              {errorMsg && <div className="freelancer-error">{errorMsg}</div>}

              <label className="freelancer-check">
                <input
                  type="checkbox"
                  checked={aceitePagamento}
                  onChange={(e) => setAceitePagamento(e.target.checked)}
                  disabled={loading}
                  required
                />
                <span style={{ fontFamily: "var(--eco-font)" }}>
                  Estou de acordo que o pagamento será realizado até as{" "}
                  <strong>14:00</strong> do próximo dia.
                </span>
              </label>

              <button className="freelancer-submit" type="submit" disabled={!canSubmit}>
                {loading ? "Enviando..." : "Cadastrar"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreelancerPage;
